import type { Unit } from '@app/src/types/resourceExplorer'

const SUFFIXES = [
  { symbol: 'T', value: 1_000_000_000_000 },
  { symbol: 'B', value: 1_000_000_000 },
  { symbol: 'M', value: 1_000_000 },
  { symbol: 'k', value: 1_000 },
]

// If you add a new currency here, make sure it also exists in the UnitTypes table in the database, with the same symbol
const CURRENCIES_FOR_SPEND: { symbol: string; currencyString: (s: string) => string }[] = [
  { symbol: 'EUR', currencyString: s => `€${s}` },
  { symbol: 'USD', currencyString: s => `$${s}` },
  { symbol: 'GBP', currencyString: s => `£${s}` },
  { symbol: 'SEK', currencyString: s => `${s} kr` },
  { symbol: 'NOK', currencyString: s => `${s} kr` },
  { symbol: 'DKK', currencyString: s => `${s} kr.` },
]

export const CURRENCY_SYMBOLS_FOR_SPEND = CURRENCIES_FOR_SPEND.map(c => c.symbol)

// Math.round and toFixed disagree on negative halves (-1.5 -> -1 vs -2), so whole amounts keep
// the Math.round they have always used.
const round = (amount: number, maximumFractionDigits: number) =>
  maximumFractionDigits === 0
    ? Math.round(amount).toString()
    : Number(amount.toFixed(maximumFractionDigits)).toString()

const addCurrencySuffix = (amount: number, maximumFractionDigits = 0) => {
  const suffixToUse = SUFFIXES.find(s => amount >= s.value)
  if (!suffixToUse) return round(amount, maximumFractionDigits)
  return `${round(amount / suffixToUse.value, maximumFractionDigits)}${suffixToUse.symbol}`
}

const wrapWithCurrency = (text: string, currencyUnit?: Unit): string => {
  if (!currencyUnit?.symbol) return text
  const uppercaseCurrencySymbol = currencyUnit.symbol.toUpperCase()
  const matchingCurrency = CURRENCIES_FOR_SPEND.find(c => c.symbol === uppercaseCurrencySymbol)
  if (!matchingCurrency) return `${text} ${currencyUnit.symbol}`
  return matchingCurrency.currencyString(text)
}

export const toCurrencyString = (amount: number, currencyUnit?: Unit) =>
  wrapWithCurrency(addCurrencySuffix(amount), currencyUnit)

export const toCompactAmountString = (amount: number) => addCurrencySuffix(amount, 1)

// Exact amount (grouped, no suffix rounding) with the currency symbol — for confirming a user-typed
// range value, where the rounded suffix form (e.g. "1.5M" -> "2M") would be misleading.
export const toExactCurrencyString = (amount: number, currencyUnit?: Unit) =>
  wrapWithCurrency(amount.toLocaleString(), currencyUnit)

// Inverse of addCurrencySuffix for user-typed range inputs: "200k" -> 200000, "1.5M" -> 1500000,
// "800" -> 800. Case-insensitive; tolerates a leading currency symbol, spaces and thousands separators.
// Returns undefined for empty or unparseable input.
export const parseCurrencyInput = (input: string): number | undefined => {
  const normalized = input.trim().toLowerCase().replace(/[\s,]/g, '')
  if (!normalized) return undefined

  // Strip a leading currency symbol but keep a minus sign so negatives aren't silently flipped to +.
  const match = normalized.match(/^[^\d.-]*(-?[\d.]+)([tbmk])?$/)
  if (!match) return undefined

  const amount = Number(match[1])
  if (Number.isNaN(amount)) return undefined

  const suffix = SUFFIXES.find(s => s.symbol.toLowerCase() === match[2])
  return Math.round(amount * (suffix?.value ?? 1))
}
